<script lang="ts" setup>
import type BalmStep from '@/vue/atoms/stepper/balm-step.vue';
import {computed, inject, ref} from 'vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {findScrollContainer, revealScrollTop} from '@/ts/composables/pure/use-scroll-to';
import {authCardOverlaysKey} from '@/ts/composables/pure/use-auth-card-overlays';

const model = defineModel<number>();

const slots = defineSlots<{
  default(): typeof BalmStep;
  'cta-actions'(): unknown;
  'cta-note'?(): unknown;
}>();

const emit = defineEmits<{
  next: [number],
  back: [],
}>();

const props = defineProps<{
  showBackButton: boolean;
  showNextButton: boolean;
  disableNextButton?: boolean;
  // When set, the entire footer (including back navigation) is delegated to the
  // `cta-actions` slot so the active step can render its own footer controls
  // (e.g. the consent step's two-row, four-button footer).
  useCustomCta?: boolean;
  // Pins the footer to the visible bottom while the step content scrolls.
  // Per design only the personalize and consent steps opt in.
  stickyFooter?: boolean;
  // i18n key for the default Next button label (defaults to "Continue").
  nextLabelKey?: string;
}>();

const allSlots = computed(() => {
  // slots includes also items that are hidden with v-if, here we filter out.
  // eslint-disable-next-line
  return (slots.default?.() ?? []).filter((slot: any) => Boolean(slot?.props));
});

// How close a field may sit to an edge of the visible band before it counts as needing moving.
const FOCUS_SCROLL_GAP_PX = 16;

// Absent wherever the stepper is used outside a card that pins anything over itself.
const overlays = inject(authCardOverlaysKey, null);

const ctaBarRef = ref<HTMLElement | null>(null);

/**
 * Scrolls `field` into the band of the card the reader can actually see, or leaves the card alone
 * when it is already there. The arithmetic lives in `revealScrollTop`; this resolves what overlays
 * the band.
 *
 * Two things can be pinned over it: the stepper's own footer, which it holds a ref to, and a
 * banner the surrounding template pins and hands down.
 */
function revealField(field: HTMLElement): void {
  const container = findScrollContainer(field);
  if (!container) {
    return;
  }
  const containerRect = container.getBoundingClientRect();
  // Only a sticky bar overlays the content; in flow it scrolls away with everything else.
  const overlayBottom = ctaBarRef.value && props.stickyFooter
    ? ctaBarRef.value.getBoundingClientRect().top
    : null;
  // The partner banner collapses to a strip pinned at the top of the same scroll container, so it
  // hides content there exactly as the footer does at the bottom. Which element that is belongs to
  // the template pinning it, and is handed over rather than looked up here.
  const banner = overlays?.banner() ?? null;
  const overlayTop = banner ? banner.getBoundingClientRect().bottom : null;

  const fieldRect = field.getBoundingClientRect();
  const top = revealScrollTop({
    container: {top: containerRect.top, bottom: containerRect.bottom, scrollTop: container.scrollTop},
    field: {top: fieldRect.top, bottom: fieldRect.bottom},
    overlayTop,
    overlayBottom,
    gap: FOCUS_SCROLL_GAP_PX,
  });
  if (top === null) {
    return;
  }
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  container.scrollTo({top, behavior: reducedMotion ? 'auto' : 'smooth'});
}

/**
 * The browser scrolls a newly focused field into the scrollport on its own, but stops as soon as
 * it is inside — which can be behind the sticky footer. Tabbing, the mobile keyboard's "next" and
 * the date segments' auto-advance all land that way, so this runs after the native scroll and
 * closes whatever gap is left (IPD-3116).
 */
function onFieldFocus(event: FocusEvent): void {
  const field = (event.target as HTMLElement | null)?.closest<HTMLElement>('.q-field');
  if (!field) {
    return;
  }
  // Two frames, so the measurement happens after the field has taken its focused height (Quasar
  // opens the area holding its validation message on focus, which happens after the event). One
  // pass only: a second, corrective scroll reads as a flicker — the view walks one way and then
  // the other before settling — and centring already absorbs the few pixels a late reflow moves.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      revealField(field);
    });
  });
}

</script>
<template>
  <div class="stepper" @focusin="onFieldFocus">
    <div class="stepper-body">
      <q-carousel
        v-model="model"
        transition-prev="slide-right"
        transition-next="slide-left"
        animated
        class="q-carousel"
      >
        <q-carousel-slide
          v-for="currentStep in allSlots"
          :key="currentStep.props.index"
          :name="currentStep.props.index"
          class="carousel-slide"
        >
          <component :is="currentStep"/>
        </q-carousel-slide>
      </q-carousel>
      <div
        v-if="useCustomCta || showBackButton || showNextButton"
        ref="ctaBarRef"
        class="cta-bar"
        :class="{'cta-bar--custom': useCustomCta, 'cta-bar--sticky': stickyFooter}"
      >
        <slot v-if="useCustomCta" name="cta-actions"/>
        <!--
          Ahead of the buttons in the DOM and below them on screen (see `order`): a note that
          qualifies an action has to reach a screen reader before the action, not after it.
        -->
        <div v-if="slots['cta-note']" class="cta-note">
          <slot name="cta-note"/>
        </div>
        <div v-if="!useCustomCta" class="buttons">
          <balm-btn
            v-if="showBackButton"
            paddings="p3-7"
            theme="grey"
            class="back"
            :title="$t('back')"
            icon="arrow-left.svg"
            @mousedown.prevent
            @click="emit('back')"
          />
          <div class="action-buttons">
            <balm-btn
              v-if="showNextButton"
              :label="$t(nextLabelKey ?? 'continue')"
              theme="blue"
              icon="arrow-right.svg"
              :disable="disableNextButton"
              @click="emit('next', model!)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stepper {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.q-carousel {
  background-color: inherit;
  height: auto;
  // The carousel has to clip — that is how a slide leaves the frame — but its edge fell exactly
  // where the full-width buttons begin, so it cut the focus ring off them. The ring reaches 4px
  // (2px of outline, 2px out) and the glow behind it the same 4px. Padding moves the clip edge out
  // by that much and the negative margin puts the content back where it was, so nothing shifts.
  padding: 4px;
  margin: -4px;
}

// The auth card owns the (single, intentional) scroll. Quasar wraps each carousel panel in a
// `.q-panel.scroll` with overflow:auto, and the panel grows with its content, so it was left to
// paint a second, dead scrollbar track next to the card's on OSes set to always show scrollbars.
//
// "Grows with its content" turned out to be true to within a rounding error: a field that expands
// as it takes focus (Quasar opens the area holding its validation message) pushed the panel two
// pixels past its own height, which is enough for it to count as scrollable — and then anything
// looking for the nearest scrollable ancestor found the panel instead of the card and scrolled two
// pixels' worth of nothing (IPD-3116). `visible` settles both: no phantom track, and nothing in a
// step that can scroll but the card. Not `hidden`, which would keep clipping whatever is painted
// outside the panel for no reason — though that buys nothing visually here, since `.q-carousel`
// clips on the same edge and has to, for its slide transition.
:deep(.q-panel.scroll) {
  overflow: visible;
}

.carousel-slide {
  padding: 0;
  background-color: initial;
}

.stepper-body {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);
}

.cta-bar {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: var(--Spacing-spacing-4);
  padding-top: var(--Spacing-spacing-4);
  padding-bottom: var(--Spacing-spacing-6);
  // Solid (not transparent) to mask content scrolling under the sticky bar;
  // must match the auth card's ui-0 surface.
  background: var(--Neutral-UI-neutral-ui-0);
}

// A step's note sits under the buttons rather than beside them, so it takes a row of its own —
// and `order` puts it there visually while it stays ahead of them in the DOM, where a note that
// qualifies an action belongs.
.cta-note {
  flex: 1 0 100%;
  order: 1;
}

.cta-bar--sticky {
  position: sticky;
  bottom: 0;
}

// When a step owns its footer (consent step), the slot lays out its own
// controls and should span the full bar width.
.cta-bar--custom {
  display: block;
}

.buttons {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-4);
  width: 100%;
}

// Back stays a fixed-width control pinned to the left; the primary action
// (typically Continue) grows to fill the remaining width on the right —
// mirrors the consent step's footer layout.
.buttons :deep(.back) {
  flex: 0 0 auto;
}

.action-buttons {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  gap: var(--Spacing-spacing-4);
}

.action-buttons :deep(.balm-btn) {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
}

</style>
